<script>
   /**************************************************************/
   /* DistancePlot                                               */
   /* --------------------                                       */
   /* shows plot with score, orthogonal or full distances        */
   /* for objects or variables                                   */
   /**************************************************************/

   import { Vector } from 'mdatools/arrays';
   import { max } from 'mdatools/stat';
   import { qchisq } from 'mdatools/distributions';
   import { Axes, XAxis, YAxis, Box, Legend, Points, Lines, TextLabels, Bars, Rectangles } from 'svelte-plots-basic/2d';
   import { PNG_WIDTH, PNG_HEIGHT, PNG_RES, MARGINS, LINE_COLORS, FACE_COLORS, LABELS_COLOR, LABELS_COLOR_SELECTED, MAX_LABEL_SYMBOLS } from '../../../shared/plots/Defaults.js';

   export let params;                     // JSON with main plot parameters
   export let res;                        // JSON with SIMCA results
   export let ncomp;                      // number of components selected by user
   export let selected = undefined;       // selected sample

   // parameters for saving the plot to file
   export let fileName = null;
   export let pngWidth = PNG_WIDTH;
   export let pngHeight = PNG_HEIGHT;
   export let pngRes = PNG_RES;

   let distType = '';

   /*****************************************/
   /* Component code                        */
   /*****************************************/

   // constant plot parameters
   const DIFF_COLOR = '#b0b0b0';
   const showGrid = true;
   const LINE_COLOR = '#808080';    // color of the lines

   // local copies of plot parameters
   let showLabels = 'no';
   let resID = 'both';
   let yMax = 0;
   let scope = 'objects';


   /**
    * Get limits for the selected distance type
    * @param res - SIMCA result object
    * @param ncomp - number of components
    */
   function getLimits(res, ncomp) {
      if (!res) return null;
      const eLim = res.v[ncomp - 1].eCrit;
      const oLim = res.v[ncomp - 1].oCrit;
      const Nf = res.v[ncomp - 1].Nf;
      return [eLim/Nf, oLim/Nf];
   }


   /**
    * Generate plot series for given result object
    * @param res - SIMCA result objects
    * @param id - result ID
    * @param distType - distance name/type
    * @param ncomp - number of components
    */
   function getSeries(res, id, distType, ncomp) {
      if (!res || !id) return null;

      // get result values for selected number of components
      const r = res[id].v[ncomp - 1];

      // get a copy of the array with distance values and its scalar u0
      const u = r[distType].slice();
      const u0 = distType === 'f' || distType === 'i' ? r['N' + distType] : r[distType + '0'];

      // generate x vector
      const n = u.length;
      const x = Vector.seq(1, n);

      // normalize the distance values and find the maximum
      let maxu = 0;
      for (let i = 0; i < n; i++) {
         u[i] = u[i]/u0;
         if (maxu < u[i]) maxu = u[i];
      }

      // update the global maximum for y-axis
      yMax = maxu;

      // create legend item
      const legendItem = {label: id, marker: 1, borderColor: LINE_COLORS[id], faceColor: FACE_COLORS[id]};

      // get proper colors
      const lineColor = LINE_COLORS[id];
      const faceColor = FACE_COLORS[id];

      // get proper labels
      const labels = ['f', 'q', 'h'].includes(distType) ? res[id].objlabels : res[id].varlabels;

      // return series object
      return {
         id, x,
         y: new Vector(u),
         labels: labels.map(v => v.slice(0, MAX_LABEL_SYMBOLS)),
         legendItem,
         lineColor,
         faceColor
      };
   }


   /**
    * Get axis label for given distance type
    * @param id - distance type
    */
   function get_axis_label(id) {
      const distNames = {
         'q': 'Orthogonal distance',
         'h': 'Score distance',
         'f': 'Full distance',
         'r': 'Ortogonal distance',
         'g': 'Score distance',
         'i': 'Importance distance',
      };
      return distNames[id] + ', ' +  id + '/' + id + '0';
   };


   /**
    * Function to handle selection of a point or a bar
    * @param e - event object
    */
   function select(e) {
      if (selected === undefined) return; // do not select points if this option is not activated in parent app
      const id = parseInt(e.detail.elementID)
      const title = e.detail.seriesTitle
      if (id >= 0 && (title === 'diff' || title === 'pv' || title === 'cal' || title === 'target')) {
         selected = id;
      }
   }


   /**
    * Function to reset selection
    * @param e - event object
    */
   function resetSelection(e) {
      selected = -1;
   }


   // reactive expressions to monitor changes in plot and model parameters
   $: if (params && params.resID !== resID) resID = params.resID;
   $: if (params && params.distType !== distType) distType = params.distType;
   $: if (params && params.showLabels !== showLabels) showLabels = params.showLabels;
   $: if (params && params.scope) scope = params.scope;

   // get IDs of all provided result objects and number of observations in the first object
   $: resIDs = res ? Object.keys(res) : null;

   // create series for the selected result objects
   let series, legendItems;
   $: if (resIDs && resID && distType && res && ncomp) {
      series = resID === 'both' || resID === 'diff' ?
         resIDs.map(id => getSeries(res, id, distType, ncomp)) :
         [getSeries(res, resID, distType, ncomp)];
      legendItems = series.map(s => s.legendItem);
   } else {
      series = null;
      legendItems = null;
   }

   // if resID is 'diff' compute absolute difference between the first two series
   let diff;
   $: if (series && resID === 'diff' && series.length === 2) {
         const d = Vector.zeros(series[0].y.length);
         for (let i = 0; i < d.length; i++) {
            d.v[i] = Math.abs(series[0].y.v[i] - series[1].y.v[i]);
         }
         diff = d;
         yMax = max(diff);
         legendItems = [{label: 'abs(cal - pv)', marker: 1, borderColor: 'transparent', faceColor: DIFF_COLOR}];
      };

   // get limits for the selected distance type
   $: [eLim, oLim] = res && resID && resID !== 'diff' && distType === 'f' ? getLimits(res[resIDs[0]], ncomp) : [0, 0];

   // reactive calculations for axis labels and title
   $: title = (scope === 'objects' ? 'Objects' : 'Variables') + ' distance plot (A = ' + ncomp + ')';
   $: xLabel = scope === 'objects' ? 'Objects' : 'Variables';
   $: yLabel = distType ? get_axis_label(distType) : '';

   // reactive calculations for axis limits
   $: nX = series && series[0].x ? series[0].x.length : 1;
   $: limX = [0 - nX * 0.05, nX * 1.05];
   $: limY = [0 - Math.max(yMax, oLim) * 0.05, 1.20 * Math.max(yMax, oLim)];
</script>

{#if res && params}
<div class="plot-container">
<Axes
   margins={MARGINS}
   downloadLinks="hover"
   on:markerclick={select}
   on:barclick={select}
   on:axesclick={resetSelection}
   fileName={fileName ? fileName : 'plot-' + scope + '-distance'}
   {xLabel} {yLabel} {limX} {limY} {title}
   {pngWidth} {pngHeight} {pngRes}
>

   {#if eLim && oLim && eLim > 0}
   <Lines xValues={limX} yValues={[eLim, eLim]} lineColor={LINE_COLOR} lineWidth={1.5} lineType={2} />
   <Lines xValues={limX} yValues={[oLim, oLim]} lineColor={LINE_COLOR} lineWidth={1.5} lineType={3} />
   {/if}

   {#if resID !== 'diff'}

      <!-- distance points -->
      {#each series as s}
      <Points
         title={s.id}
         xValues={s.x}
         yValues={s.y}
         borderWidth={1.5}
         faceColor={s.faceColor}
         borderColor={s.lineColor}
      />
      {/each}

      <!-- labels on top of each point -->
      {#if showLabels === 'show'}
         {#each series as s}
         <TextLabels
            xValues={s.x}
            yValues={s.y}
            labels={s.labels}
            pos={3}
            textSize={0.8}
            faceColor={LABELS_COLOR}
         />
         {/each}
      {/if}

      <!-- for selection highlight selected point and dim the others -->
      {#if selected >= 0}
         <Rectangles
            left={[limX[0]]}
            top={[limY[1]]}
            width={[limX[1] - limX[0]]}
            height={[limY[1] - limY[0]]}
            borderColor='transparent'
            faceColor='#ffffffa0'
         />

         {#each series as s}
            <Points
               title={s.id}
               markerSize={1.25}
               xValues={[s.x.v[selected]]}
               yValues={[s.y.v[selected]]}
               borderWidth={3.0}
               faceColor={s.faceColor}
               borderColor={s.lineColor}
            />
         {/each}

         {#if showLabels === 'show'}
            {#each series as s}
               <TextLabels
                  xValues={[s.x.v[selected]]}
                  yValues={[s.y.v[selected]]}
                  labels={[s.labels[selected]]}
                  pos={3}
                  textSize={0.8}
                  faceColor={LABELS_COLOR_SELECTED}
               />
            {/each}
         {/if}

      {/if}

   {:else}

      <!-- bars with difference values -->
      <Bars
         title='diff'
         xValues={series[0].x}
         yValues={diff}
         faceColor={DIFF_COLOR}
         borderColor='transparent'
      />

      <!-- labels on top of each bar -->
      {#if showLabels === 'show'}
         <TextLabels
            xValues={series[0].x}
            yValues={diff}
            labels={series[0].labels}
            pos={3}
            textSize={0.8}
            faceColor={LABELS_COLOR}
         />
      {/if}

      <!-- for selection highlight selected bar and dim the others -->
      {#if selected >= 0}

         <Rectangles
            left={[limX[0]]}
            top={[limY[1]]}
            width={[limX[1] - limX[0]]}
            height={[limY[1] - limY[0]]}
            borderColor='transparent'
            faceColor='#ffffffa0'
         />

         <Rectangles title='diff'
            left={[series[0].x.v[selected] - 0.5]}
            top={[diff.v[selected]]}
            width={1}
            height={[diff.v[selected]]}
            faceColor='#ff0000'
            borderColor='transparent'
         />

         {#if showLabels === 'show'}
            <TextLabels
               xValues={[series[0].x.v[selected]]}
               yValues={[diff.v[selected]]}
               labels={[series[0].labels[selected]]}
               pos={3}
               textSize={0.8}
               faceColor={LABELS_COLOR_SELECTED}
            />
         {/if}

      {/if}

   {/if}


   {#if legendItems && legendItems.length > 0}
   <Legend items={legendItems} position='topright'/>
   {/if}

   <XAxis {showGrid} slot="xaxis" />
   <YAxis {showGrid} slot="yaxis" />
   <Box slot="box" />
</Axes>
</div>

{#if selected !== undefined}
   {#if selected < 0}
   <div class="msg-select">Click on a {resID === 'diff' ? 'bar' : 'point' } to select...</div>
   {:else}
   <div class="msg-select">Click anywhere to remove selection...</div>
   {/if}
{/if}
{/if}


<style>
   .plot-container {
      width: 100%;
      height: calc(100% - 15px);
   }
   .msg-select {
      height: 15px;
      font-size: 12px;
      color: #aaa;
      text-align: center;
   }
</style>

